/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useLogProfileData.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for loading log profile data and menu items.
    Uses moduleSlug (string) for permissions instead of numeric moduleId.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { getMenuItemsFlat, MenuItemFlat } from '../../../config/menuDefinition';
import { MenuItem } from '../types';
import { fetchLogProfileById } from '../api';

export interface LogPermission {
  moduleSlug: string;
  logCreate: boolean;
  logEdit: boolean;
  logDelete: boolean;
  notifyCreate: boolean;
  notifyEdit: boolean;
  notifyDelete: boolean;
}

export function useLogProfileData(
  mode: 'create' | 'edit',
  id: string | undefined,
  duplicateId: string | null,
  setFormData: (data: { name: string; description: string; permissions: LogPermission[] }) => void,
  setOriginalData: (data: { name: string; description: string; permissions: LogPermission[] }) => void,
  resetParam?: string | null,
) {
  const { user } = useAuthStore();
  const { t } = useI18n();
  const { showError, showInfo } = useNotifications();
  const tenantSlug = user?.tenant?.slug || 'main';
  const isMainTenant = tenantSlug === 'main';

  // MenuItemFlat is used for rendering the module tree in the permissions table
  const [menuItems, setMenuItems] = useState<MenuItemFlat[]>([]);
  const [menuItemsReady, setMenuItemsReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const profileLoadedRef = useRef<string | null>(null);
  const formResetRef = useRef<boolean>(false);
  const lastIdRef = useRef<string | undefined>(id);
  const resetParamProcessedRef = useRef<string | null>(null);
  const menuItemsRef = useRef<MenuItemFlat[]>([]);

  // Use refs for functions to avoid re-renders
  const setFormDataRef = useRef(setFormData);
  const setOriginalDataRef = useRef(setOriginalData);
  const showErrorRef = useRef(showError);
  const showInfoRef = useRef(showInfo);

  // Update refs when functions change
  useEffect(() => {
    setFormDataRef.current = setFormData;
    setOriginalDataRef.current = setOriginalData;
    showErrorRef.current = showError;
    showInfoRef.current = showInfo;
  }, [setFormData, setOriginalData, showError, showInfo]);

  // Update menuItemsRef when menuItems changes
  useEffect(() => {
    menuItemsRef.current = menuItems;
  }, [menuItems]);

  // Helper: collect all leaf slugs
  const collectLeafSlugs = (items: MenuItemFlat[]): string[] => {
    const slugs: string[] = [];
    items.forEach(item => {
      if (item.level === 3 && item.slug) {
        slugs.push(item.slug);
      } else if (item.level === 2 && item.slug) {
        const hasChildren = items.some(
          child => child.parentId === item.id && child.level === 3
        );
        if (!hasChildren) {
          slugs.push(item.slug);
        }
      }
    });
    return slugs;
  };

  // Load menu items from the hardcoded definition — synchronous, no API call needed
  useEffect(() => {
    const flat = getMenuItemsFlat(t, isMainTenant);
    setMenuItems(flat);
    menuItemsRef.current = flat;
    setMenuItemsReady(true);
  // Re-run only if the translation function identity changes (language switch)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // Load profile data for edit or duplicate mode
  useEffect(() => {
    const loadProfile = async () => {
      const loadId = mode === 'edit' ? id : duplicateId;
      const currentId = id || 'new';
      const previousId = lastIdRef.current;
      const idChangedToNew = previousId && previousId !== 'new' && currentId === 'new';
      const hasResetParam = !!resetParam && resetParam !== resetParamProcessedRef.current;

      // If reset param is present and not yet processed, force reset
      if (hasResetParam) {
        formResetRef.current = false;
        profileLoadedRef.current = null;
        // Clear the lastIdRef to ensure we detect the change
        lastIdRef.current = undefined;
        // Mark this reset param as processed
        resetParamProcessedRef.current = resetParam;
      }

      if (!loadId || loadId === 'new') {
        // Reset the flag if we were previously loading a profile OR if ID changed to 'new' OR if reset param is present
        if (hasResetParam || idChangedToNew || profileLoadedRef.current !== null) {
          formResetRef.current = false;
        }
        profileLoadedRef.current = null;

        // Reset form when navigating to new (either initial load or after save)
        // Always reset if resetParam is present, or if formResetRef is false, or if ID changed to new
        if ((hasResetParam || !formResetRef.current || idChangedToNew) && menuItemsRef.current.length > 0) {
          formResetRef.current = true;
          lastIdRef.current = currentId;

          // Collect leaf slugs for initialization
          const leafSlugs = collectLeafSlugs(menuItemsRef.current);

          // Create permissions array with all modules initialized with all permissions false
          const allPermissions = leafSlugs.map((slug) => ({
            moduleSlug: slug,
            logCreate: false,
            logEdit: false,
            logDelete: false,
            notifyCreate: false,
            notifyEdit: false,
            notifyDelete: false,
          }));

          setFormDataRef.current({
            name: '',
            description: '',
            permissions: allPermissions,
          });
          setOriginalDataRef.current({
            name: '',
            description: '',
            permissions: allPermissions.map((p) => ({ ...p })),
          });
        } else {
          lastIdRef.current = currentId;
        }
        return;
      }

      // Wait for menu items to be loaded before loading profile
      // This ensures we can properly merge permissions with all available modules
      if (menuItemsRef.current.length === 0) return;

      // Reset the form reset flag when loading an actual profile
      formResetRef.current = false;

      // Prevent duplicate API calls
      if (profileLoadedRef.current === loadId) {
        lastIdRef.current = currentId;
        return;
      }

      profileLoadedRef.current = loadId;
      lastIdRef.current = currentId;

      try {
        setLoading(true);
        const profile = await fetchLogProfileById(loadId, tenantSlug, user?.id);

        // Collect leaf slugs
        const leafSlugs = collectLeafSlugs(menuItemsRef.current);

        // Create a map of existing permissions from the profile by moduleSlug
        const existingPermissionsMap = new Map<string, LogPermission>();
        (profile.permissions || []).forEach((p: LogPermission) => {
          existingPermissionsMap.set(p.moduleSlug, p);
        });

        // Merge existing permissions with all available modules
        const mergedPermissions = leafSlugs.map((slug) => {
          const existing = existingPermissionsMap.get(slug);
          if (existing) {
            return {
              moduleSlug: slug,
              logCreate: existing.logCreate ?? false,
              logEdit: existing.logEdit ?? false,
              logDelete: existing.logDelete ?? false,
              notifyCreate: existing.notifyCreate ?? false,
              notifyEdit: existing.notifyEdit ?? false,
              notifyDelete: existing.notifyDelete ?? false,
            };
          }
          return {
            moduleSlug: slug,
            logCreate: false,
            logEdit: false,
            logDelete: false,
            notifyCreate: false,
            notifyEdit: false,
            notifyDelete: false,
          };
        });

        const loadedData = {
          name: mode === 'edit' ? profile.name : `${profile.name}_copy`,
          description: profile.description || '',
          permissions: mergedPermissions,
        };

        setFormDataRef.current(loadedData);
        // Create a deep copy for originalData to avoid shared references
        setOriginalDataRef.current({
          name: loadedData.name,
          description: loadedData.description,
          permissions: loadedData.permissions.map((p: LogPermission) => ({ ...p })),
        });

        // Show duplicate hint notification
        if (duplicateId) {
          showInfoRef.current(t('logProfiles.messages.duplicateHint'));
        }
      } catch (error) {
        showErrorRef.current(error instanceof Error ? error.message : t('logProfiles.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id, duplicateId, tenantSlug, user?.id, resetParam, menuItemsReady]);

  return {
    menuItems,
    loading,
  };
}
